import {
  parseFitAssessmentCanvasInput,
  serializeFitAssessmentCanvasPayload,
} from '@/components/fit-designs/canvas'
import {
  defaultFitResponseVariantId,
  fitResponseVariantRegistry,
} from '@/components/fit-designs/registry'
import type { FitAssessmentDesignSample } from '@/components/fit-designs/samples'
import {
  defaultFitAssessmentDesignSample,
  fitAssessmentDesignSamples,
} from '@/components/fit-designs/samples'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Code2,
  Columns3,
  LayoutTemplate,
  Monitor,
  Smartphone,
} from 'lucide-react'
import { startTransition, useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

type PreviewWidthMode = 'desktop' | 'mobile'

const leftVariant =
  fitResponseVariantRegistry.find((variant) => variant.id === defaultFitResponseVariantId) ??
  fitResponseVariantRegistry[0]
const rightVariants = fitResponseVariantRegistry.filter((variant) => variant.id !== leftVariant.id)

const getSamplePayload = (sample: FitAssessmentDesignSample) =>
  serializeFitAssessmentCanvasPayload(sample.payload)

export function FitAssessmentDesignsPage() {
  const [selectedSampleId, setSelectedSampleId] = useState(defaultFitAssessmentDesignSample.id)
  const [previewWidthMode, setPreviewWidthMode] = useState<PreviewWidthMode>('desktop')
  const [rightVariantId, setRightVariantId] = useState(rightVariants[0]?.id ?? leftVariant.id)
  const [canvasInput, setCanvasInput] = useState(getSamplePayload(defaultFitAssessmentDesignSample))
  const [renderedResult, setRenderedResult] = useState(
    defaultFitAssessmentDesignSample.payload.data
  )
  const deferredCanvasInput = useDeferredValue(canvasInput)

  const selectedSample = useMemo(
    () =>
      fitAssessmentDesignSamples.find((sample) => sample.id === selectedSampleId) ??
      defaultFitAssessmentDesignSample,
    [selectedSampleId]
  )

  const parsedCanvas = useMemo(
    () => parseFitAssessmentCanvasInput(deferredCanvasInput),
    [deferredCanvasInput]
  )
  const parsedCanvasError = 'error' in parsedCanvas ? parsedCanvas.error : null

  useEffect(() => {
    if (!parsedCanvas.success) return
    setRenderedResult(parsedCanvas.result)
  }, [parsedCanvas])

  const activeRightVariant =
    rightVariants.find((variant) => variant.id === rightVariantId) ??
    rightVariants[0] ??
    leftVariant
  const LeftPreview = leftVariant.component
  const RightPreview = activeRightVariant.component
  const previewWidthClass = previewWidthMode === 'mobile' ? 'mx-auto max-w-[390px]' : 'w-full'

  const handleSampleSelect = (sample: FitAssessmentDesignSample) => {
    setSelectedSampleId(sample.id)
    setRenderedResult(sample.payload.data)
    startTransition(() => {
      setCanvasInput(getSamplePayload(sample))
    })
  }

  const handleFormatJson = () => {
    const parsed = parseFitAssessmentCanvasInput(canvasInput)
    if (!parsed.success) return
    setCanvasInput(
      serializeFitAssessmentCanvasPayload(
        parsed.sourceKind === 'wrapped-response'
          ? {
              success: true,
              data: parsed.result,
            }
          : parsed.result
      )
    )
  }

  return (
    <div className="min-h-screen bg-background selection:bg-blue-100 dark:selection:bg-blue-900">
      <header className="sticky top-0 z-40 border-b glass">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <Button asChild variant="outline" size="sm" className="rounded-full">
              <Link to="/">
                <ArrowLeft className="w-4 h-4" />
                Portfolio
              </Link>
            </Button>
            <div className="min-w-0">
              <p className="meta-label text-blue-600 dark:text-blue-400">Design Lab</p>
              <h1 className="truncate text-lg font-bold tracking-tight">
                Fit Response Comparisons
              </h1>
            </div>
          </div>
          <ThemeToggle className="relative top-0 right-0 shrink-0" />
        </div>
      </header>

      <main className="mx-auto flex max-w-7xl flex-col gap-8 px-4 py-8 sm:px-6 lg:px-8">
        <section className="rounded-[2rem] border border-slate-200 bg-gradient-to-br from-white via-blue-50/40 to-slate-50 p-6 shadow-xl dark:border-slate-800 dark:from-slate-950 dark:via-slate-950 dark:to-blue-950/40 md:p-8">
          <div className="grid gap-8 xl:grid-cols-[360px_1fr]">
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300">
                  <LayoutTemplate className="w-3.5 h-3.5" />
                  Canvas Controls
                </div>
                <h2 className="text-3xl font-black tracking-tight md:text-4xl">
                  Design canvas for fit-response UI experiments
                </h2>
                <p className="max-w-md text-sm leading-6 text-muted-foreground">
                  Swap sample payloads, paste real API responses, and compare the live production
                  renderer against alternate concepts without touching the recruiter-facing flow.
                </p>
              </div>

              <Card className="glass-darker overflow-hidden border-2">
                <CardContent className="space-y-5 p-5">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Columns3 className="w-4 h-4 text-blue-600" />
                      <p className="text-sm font-semibold">Sample payloads</p>
                    </div>
                    <div className="grid gap-2">
                      {fitAssessmentDesignSamples.map((sample) => (
                        <button
                          key={sample.id}
                          type="button"
                          onClick={() => handleSampleSelect(sample)}
                          aria-pressed={sample.id === selectedSample.id}
                          className={cn(
                            'rounded-2xl border px-4 py-3 text-left transition-all',
                            sample.id === selectedSample.id
                              ? 'border-blue-500 bg-blue-50 shadow-sm dark:border-blue-500/60 dark:bg-blue-950/40'
                              : 'border-border/80 bg-background/80 hover:border-blue-300 hover:bg-blue-50/60 dark:hover:border-blue-700 dark:hover:bg-blue-950/30'
                          )}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1">
                              <p className="text-sm font-semibold">{sample.label}</p>
                              <p className="text-xs leading-5 text-muted-foreground">
                                {sample.description}
                              </p>
                            </div>
                            {sample.id === selectedSample.id && (
                              <CheckCircle2 className="mt-0.5 w-4 h-4 text-blue-600" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-blue-600" />
                      <p className="text-sm font-semibold">Preview width</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {(
                        [
                          { id: 'desktop', label: 'Desktop', icon: Monitor },
                          { id: 'mobile', label: 'Mobile', icon: Smartphone },
                        ] as const
                      ).map((mode) => (
                        <button
                          key={mode.id}
                          type="button"
                          onClick={() => setPreviewWidthMode(mode.id)}
                          aria-pressed={mode.id === previewWidthMode}
                          className={cn(
                            'inline-flex items-center justify-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors',
                            mode.id === previewWidthMode
                              ? 'border-blue-500 bg-blue-600 text-white'
                              : 'border-border bg-background hover:bg-accent hover:text-accent-foreground'
                          )}
                        >
                          <mode.icon className="w-4 h-4" />
                          {mode.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Code2 className="w-4 h-4 text-blue-600" />
                      <p className="text-sm font-semibold">Canvas status</p>
                    </div>
                    <div className="rounded-2xl border border-border/80 bg-background/80 p-4">
                      {parsedCanvas.success ? (
                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-foreground">
                            Rendering valid payload
                          </p>
                          <p className="text-xs leading-5 text-muted-foreground">
                            Source format:{' '}
                            <span className="font-semibold text-foreground">
                              {parsedCanvas.sourceKind}
                            </span>
                            . Updates apply automatically as soon as the JSON becomes valid again.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                            Using last valid payload
                          </p>
                          <p className="text-xs leading-5 text-muted-foreground">
                            {parsedCanvasError}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="overflow-hidden border-2 glass-darker shadow-2xl">
              <CardContent className="space-y-4 p-0">
                <div className="flex flex-col gap-3 border-b border-border/70 bg-slate-50/90 px-5 py-4 md:flex-row md:items-center md:justify-between md:px-6 dark:bg-slate-950/40">
                  <div>
                    <p className="meta-label text-blue-600 dark:text-blue-300">Payload Editor</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Paste a full `/api/analyze-fit` response or the inner `data` object.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => setCanvasInput(getSamplePayload(selectedSample))}
                    >
                      Reset sample
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={handleFormatJson}
                    >
                      Format JSON
                    </Button>
                  </div>
                </div>

                <div className="rounded-b-[1.5rem] bg-background/80 p-4 md:p-6">
                  <Textarea
                    value={canvasInput}
                    onChange={(event) => setCanvasInput(event.target.value)}
                    spellCheck={false}
                    className="min-h-[460px] bg-background font-mono text-xs leading-6 shadow-inner"
                    aria-label="Fit assessment design canvas JSON"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-2">
          <Card className="overflow-hidden border-2 shadow-xl glass-darker">
            <CardContent className="space-y-5 p-5 md:p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="meta-label text-blue-600 dark:text-blue-400">Left Comparison</p>
                  <h2 className="mt-1 text-xl font-bold tracking-tight">
                    Production renderer &mdash; {leftVariant.label}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    {leftVariant.description}
                  </p>
                </div>
                <span className="rounded-full border border-border bg-background/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Locked
                </span>
              </div>

              <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50/80 p-4 shadow-inner dark:border-slate-800 dark:bg-slate-950/30">
                <div className={cn('transition-all duration-300', previewWidthClass)}>
                  <LeftPreview result={renderedResult} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden border-2 shadow-xl glass-darker">
            <CardContent className="space-y-5 p-5 md:p-6">
              <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="meta-label text-blue-600 dark:text-blue-400">Right Comparison</p>
                    <h2 className="mt-1 text-xl font-bold tracking-tight">Variant switcher</h2>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      Use the registry below as the landing zone for new fit-response experiments as
                      we iterate.
                    </p>
                  </div>
                  <ArrowRight className="mt-1 w-5 h-5 text-blue-600" />
                </div>

                <div className="flex flex-wrap gap-2">
                  {rightVariants.map((variant) => (
                    <button
                      key={variant.id}
                      type="button"
                      onClick={() => setRightVariantId(variant.id)}
                      aria-pressed={variant.id === activeRightVariant.id}
                      className={cn(
                        'rounded-full border px-4 py-2 text-sm font-medium transition-colors',
                        variant.id === activeRightVariant.id
                          ? 'border-blue-500 bg-blue-600 text-white'
                          : 'border-border bg-background hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      {variant.label}
                    </button>
                  ))}
                </div>

                <div className="rounded-2xl border border-border/70 bg-background/80 p-4">
                  <p className="text-sm font-semibold">{activeRightVariant.label}</p>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    {activeRightVariant.description}
                  </p>
                </div>
              </div>

              <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50/80 p-4 shadow-inner dark:border-slate-800 dark:bg-slate-950/30">
                <div className={cn('transition-all duration-300', previewWidthClass)}>
                  <RightPreview result={renderedResult} />
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  )
}
